import React, { useState, useEffect } from 'react';
import './aiCallPopup.css';
import { BolnaService } from '../../../../../services/bolnaService';

const AICallPopup = ({
  isVisible,
  onClose,
  onCall,
  candidateId,
  clientId,  // Optional - provided by Admin when initiating on behalf of a client
}) => {
  const [provider, setProvider] = useState('Bolna'); // Phase 1: only Bolna is active
  const [questions, setQuestions] = useState([{ question: '', evaluationCriteria: '', excludeFromScoring: false }]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Template states
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch templates from API on mount
  useEffect(() => {
    if (isVisible) {
      fetchTemplates();
    }
  }, [isVisible]);

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await BolnaService.getQuestionTemplates(clientId || null);
      setTemplates(response.data || []);
    } catch (e) {
      console.error('Error loading templates:', e);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  if (!isVisible) return null;

  const handleAddQuestion = () => {
    setQuestions([...questions, { question: '', evaluationCriteria: '', excludeFromScoring: false }]);
  };

  const handleRemoveQuestion = (index) => {
    if (questions.length > 1) {
      const newQuestions = questions.filter((_, i) => i !== index);
      setQuestions(newQuestions);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const handleCall = async () => {
    setError('');

    // Filter out completely empty questions (no text and no criteria)
    const filteredQuestions = questions.filter(
      q => q.question.trim() !== '' || q.evaluationCriteria.trim() !== ''
    );

    if (filteredQuestions.length === 0) {
      setError('Please add at least one question.');
      return;
    }

    // Validate that if question is present, criteria is also present and vice-versa
    const hasIncompleteQuestions = filteredQuestions.some(
      q => q.question.trim() === '' || q.evaluationCriteria.trim() === ''
    );

    if (hasIncompleteQuestions) {
      setError('Please ensure every included question has both the question text and evaluation criteria.');
      return;
    }

    // Transform for API
    const candidateQuestions = filteredQuestions
      .map((q, i) => `${i + 1}. ${q.question.trim()}`)
      .join('\n');

    // API Expects: [{ question: "...", evaluationCriteria: "...", excludeFromScoring: boolean }]
    const questionEvaluations = filteredQuestions.map(q => ({
      question: q.question.trim(),
      evaluationCriteria: q.evaluationCriteria.trim(),
      excludeFromScoring: q.excludeFromScoring || false
    }));

    try {
      setIsLoading(true);

      const response = await BolnaService.initiateCall(
        candidateId,
        candidateQuestions,
        questionEvaluations,
        null,       // scheduledAt
        clientId || null  // clientId (for admin-initiated calls)
      );

      console.log('AI Call Initiated:', response.data);

      // Notify parent with response data
      if (onCall) {
        onCall({
          candidateId,
          questions: questionEvaluations,
          executionId: response.data.execution_id,
          status: response.data.status,
          provider
        });
      }

      // Close popup on success
      onClose();
    } catch (err) {
      console.error('Failed to initiate AI call:', err);
      const errorMessage = err.response?.data?.message || 'Failed to initiate call. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setQuestions([{ question: '', evaluationCriteria: '', excludeFromScoring: false }]);
    setSelectedTemplate('');
    setShowSaveTemplate(false);
    setNewTemplateName('');
    setError('');
    onClose();
  };

  // Template handlers
  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);

    if (templateId) {
      const template = templates.find(t => String(t.id) === templateId || t.name === templateId);
      if (template) {

        if (template.questions && template.questions.length > 0) {
          // Backward compatibility check for old strings array format vs new objects format
          if (typeof template.questions[0] === 'string') {
            setQuestions(
              template.questions.map(q => ({
                question: q,
                evaluationCriteria: template.evaluationCriteria || '',
                excludeFromScoring: false
              }))
            );
          } else {
            // It's the new format
            setQuestions([...template.questions]);
          }
        } else {
          setQuestions([{ question: '', evaluationCriteria: '', excludeFromScoring: false }]);
        }
      }
    } else {
      // Reset to empty if no template selected
      setQuestions([{ question: '', evaluationCriteria: '', excludeFromScoring: false }]);
    }
  };

  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim()) return;

    // Filter valid pairs
    const filteredQuestions = questions.filter(
      q => q.question.trim() !== '' && q.evaluationCriteria.trim() !== ''
    );
    if (filteredQuestions.length === 0) {
      setError('Please add at least one complete question (with criteria) before saving a template.');
      return;
    }

    const finalQuestions = filteredQuestions.map(q => ({
      question: q.question.trim(),
      evaluationCriteria: q.evaluationCriteria.trim(),
      excludeFromScoring: q.excludeFromScoring || false
    }));

    setIsSavingTemplate(true);
    try {
      const existingTemplate = templates.find(
        t => String(t.id) === selectedTemplate || t.name === selectedTemplate
      );

      if (existingTemplate && existingTemplate.id) {
        await BolnaService.updateQuestionTemplate(
          existingTemplate.id,
          newTemplateName.trim(),
          finalQuestions
        );
      } else {
        await BolnaService.createQuestionTemplate(
          newTemplateName.trim(),
          finalQuestions,
          clientId || null
        );
      }

      // Refresh templates list
      await fetchTemplates();

      setNewTemplateName('');
      setShowSaveTemplate(false);
      console.log('Template saved successfully');
    } catch (err) {
      console.error('Failed to save template:', err);
      setError('Failed to save template. Please try again.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  // const handleDeleteTemplate = async () => {
  //   if (!selectedTemplate) return;

  //   const template = templates.find(t => String(t.id) === selectedTemplate || t.name === selectedTemplate);
  //   if (!template) return;

  //   const confirmDelete = window.confirm(`Delete template "${template.name}"?`);
  //   if (!confirmDelete) return;

  //   setIsDeletingTemplate(true);
  //   try {
  //     await BolnaService.deleteQuestionTemplate(template.id || selectedTemplate);

  //     // Reset selection and refresh
  //     setSelectedTemplate('');
  //     setQuestions(['']);
  //     setEvaluationCriteria('');
  //     await fetchTemplates();
  //     console.log('Template deleted successfully');
  //   } catch (err) {
  //     console.error('Failed to delete template:', err);
  //     setError('Failed to delete template. Please try again.');
  //   } finally {
  //     setIsDeletingTemplate(false);
  //   }
  // };
  const handleDeleteTemplate = () => {
    if (!selectedTemplate) return;
    setShowDeleteConfirm(true);
  };
  const confirmDeleteTemplate = async () => {
    const template = templates.find(
      t => String(t.id) === selectedTemplate || t.name === selectedTemplate
    );
    if (!template) return;

    setIsDeletingTemplate(true);
    try {
      await BolnaService.deleteQuestionTemplate(template.id || selectedTemplate);

      setSelectedTemplate('');
      setQuestions([{ question: '', evaluationCriteria: '', excludeFromScoring: false }]);
      await fetchTemplates();

    } catch (err) {
      console.error(err);
      setError('Failed to delete template.');
    } finally {
      setIsDeletingTemplate(false);
      setShowDeleteConfirm(false);
    }
  };


  return (
    <div className="ai-call-popup-overlay">
      <div className="ai-call-popup-container">
        <div className="ai-call-popup-header">
          <h2>Initiate AI Call</h2>
        </div>

        {/* AI Provider Selection */}
        <div className="ai-call-template-section">
          <div className="ai-call-template-row">
            <label className="ai-call-template-label">Provider:</label>
            <select
              className="ai-call-template-select"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              disabled={isLoading}
            >
              <option value="Bolna">Bolna ✓ (Active)</option>
              <option value="11Labs" disabled>11 Labs (Coming Soon)</option>
            </select>
          </div>
        </div>

        {/* Template Selection */}
        <div className="ai-call-template-section">
          <div className="ai-call-template-row">
            <label className="ai-call-template-label">Template:</label>
            <select
              className="ai-call-template-select"
              value={selectedTemplate}
              onChange={handleTemplateSelect}
              disabled={isLoadingTemplates}
            >
              <option value="">{isLoadingTemplates ? 'Loading...' : '-- Select Template --'}</option>
              {templates.map((template) => (
                <option key={template.id || template.name} value={String(template.id || template.name)}>
                  {template.name}
                </option>
              ))}
            </select>
            <button
              className="ai-call-save-template-btn"
              onClick={() => {
                const next = !showSaveTemplate;

                if (next && selectedTemplate) {
                  const template = templates.find(
                    t => String(t.id) === selectedTemplate || t.name === selectedTemplate
                  );
                  if (template) {
                    setNewTemplateName(template.name);
                  }
                }

                setShowSaveTemplate(next);
              }}
              title="Save as Template"
            >
              {showSaveTemplate ? '✕' : '💾'} Save
            </button>
            {selectedTemplate && (
              <button
                className="ai-call-delete-template-btn"
                onClick={handleDeleteTemplate}
                disabled={isDeletingTemplate}
                title="Delete Template"
              >
                {isDeletingTemplate ? '...' : '🗑️'} Delete
              </button>
            )}
          </div>

          {/* Save Template Input */}
          {showSaveTemplate && (
            <div className="ai-call-save-template-row">
              <input
                type="text"
                className="ai-call-template-name-input"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Enter template name..."
              />
              <button
                className="ai-call-confirm-save-btn"
                onClick={handleSaveTemplate}
                disabled={!newTemplateName.trim() || isSavingTemplate}
              >
                {isSavingTemplate ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="ai-call-popup-content">
          <div className="ai-call-questions-tab">
            <h3 style={{ fontSize: '15px', color: '#2d3e50', marginBottom: '12px' }}>Questions & Evaluation Criteria</h3>
            <div className="ai-call-questions-list">
              {questions.map((q, index) => (
                <div key={index} className="ai-call-question-item">
                  <div className="ai-call-question-inputs">
                    <input
                      type="text"
                      className="ai-call-question-input"
                      value={q.question}
                      onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                      placeholder={`Enter question ${index + 1}...`}
                      disabled={isLoading}
                    />
                    <textarea
                      className="ai-call-criteria-inline-input"
                      value={q.evaluationCriteria}
                      onChange={(e) => handleQuestionChange(index, 'evaluationCriteria', e.target.value)}
                      placeholder={`Evaluation criteria for question ${index + 1}...`}
                      disabled={isLoading}
                      rows={2}
                    />
                    <div className="ai-call-checkbox-row mt-2">
                      <label className="ai-call-exclude-checkbox-label">
                        <input
                          type="checkbox"
                          checked={q.excludeFromScoring || false}
                          onChange={(e) => handleQuestionChange(index, 'excludeFromScoring', e.target.checked)}
                          disabled={isLoading}
                        />
                        &nbsp; Exclude from Scoring
                      </label>
                    </div>
                  </div>
                  {questions.length > 1 && (
                    <button
                      className="ai-call-remove-btn"
                      onClick={() => handleRemoveQuestion(index)}
                      title="Remove question"
                      disabled={isLoading}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button className="ai-call-add-btn" onClick={handleAddQuestion} disabled={isLoading}>
              <span className="ai-call-add-icon">+</span>
              Add Question
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="ai-call-error-message">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="ai-call-popup-actions">
          <button className="ai-call-popup-cancel" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </button>
          <button className="ai-call-popup-call" onClick={handleCall} disabled={isLoading}>
            {isLoading ? 'Calling...' : 'Call'}
          </button>
        </div>
        {showDeleteConfirm && (
          <div className="ai-delete-confirm-overlay">
            <div className="ai-delete-confirm-box">
              <p>Delete selected template?</p>

              <div className="ai-delete-confirm-actions">
                <button
                  className="ai-delete-cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>

                <button
                  className="ai-delete-confirm"
                  onClick={confirmDeleteTemplate}
                  disabled={isDeletingTemplate}
                >
                  {isDeletingTemplate ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AICallPopup;